const MSC_LINE_ID = 16;
const GRATUITY_LINE_IDS = [16, 17, 22, 73];

const priceTranslationKey = value => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

// Use the administrator's English/Korean columns, never guessed category labels.
export function buildTraveltekPriceTranslations(rows = []) {
  const translations = new Map();
  if (!Array.isArray(rows)) return translations;
  const candidates = rows.map(row => Array.isArray(row)
    ? { idx: row[0], priority: row[1], type: row[2], en: row[3], kr: row[4], used: row[6] }
    : row).filter(row => row && row.type === 'etc' && Number(row.used) === 1);
  candidates.sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0)
    || (Number(a.idx) || 0) - (Number(b.idx) || 0));
  candidates.forEach(row => {
    const key = priceTranslationKey(row.en);
    const label = String(row.kr ?? '').trim();
    if (key && label && !translations.has(key)) translations.set(key, label);
  });
  return translations;
}

export function getTraveltekPriceLabel(item = {}, translations, fallback) {
  const original = String(item?.description || item?.category || '').trim();
  return translations?.get(priceTranslationKey(original)) || fallback || original.toUpperCase();
}

function normalizePriceText(value = '') {
  return String(value || '').trim();
}

function normalizeAlphaText(value = '') {
  return normalizePriceText(value)
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

export function isMscPortChargeDescription(descriptionRaw = '', lineId = 0) {
  if (Number(lineId) !== MSC_LINE_ID) {
    return false;
  }

  const normalized = normalizeAlphaText(descriptionRaw);
  if (!normalized) {
    return false;
  }

  return normalized.includes('portcharge');
}

export function isNonCommissionableFareDescription(descriptionRaw = '') {
  const normalized = normalizeAlphaText(descriptionRaw);
  return normalized.includes('non') && normalized.includes('commission');
}

export function isTraveltekGratuityPriceItem({
  category = '',
  description = '',
  lineId = 0,
} = {}) {
  const normalizedLineId = Number(lineId);
  if (!GRATUITY_LINE_IDS.includes(normalizedLineId)) {
    return false;
  }

  const normalizedCategory = normalizeAlphaText(category);
  const normalizedDescription = normalizeAlphaText(description);

  if (['grats', 'gratuity', 'gratuities'].includes(normalizedCategory)) {
    return true;
  }

  if (!normalizedDescription) {
    return false;
  }

  if (normalizedDescription.includes('gratuity') || normalizedDescription.includes('gratuities')) {
    return true;
  }

  if (normalizedLineId === MSC_LINE_ID && normalizedDescription.includes('hotelservicecharge')) {
    return true;
  }

  return false;
}

export function isTraveltekGratuityExtra(extra, lineId = 0) {
  if (!extra || typeof extra !== 'object') {
    return false;
  }

  const normalizedLineId = Number(lineId);
  if (!GRATUITY_LINE_IDS.includes(normalizedLineId)) {
    return false;
  }

  const normalizedType = normalizeAlphaText(extra.type);
  if (normalizedLineId === MSC_LINE_ID) {
    const normalizedDescription = normalizeAlphaText(extra.description);
    return normalizedType === 'hotelservicecharges'
      || normalizedDescription.includes('hotelservicecharge');
  }

  return normalizedType === 'gratuities';
}

export function findTraveltekGratuityExtra(extras, lineId = 0) {
  if (!Array.isArray(extras)) {
    return null;
  }

  return extras.find(extra => isTraveltekGratuityExtra(extra, lineId)) || null;
}

function isSameTraveltekExtra(target, candidate, lineId) {
  if (!target || !candidate) {
    return false;
  }

  const targetResultNo = target.resultno != null ? String(target.resultno) : '';
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return targetResultNo !== '' && String(candidate) === targetResultNo;
  }
  if (typeof candidate !== 'object') {
    return false;
  }

  const candidateResultNo = candidate.resultno != null ? String(candidate.resultno) : '';
  if (targetResultNo && candidateResultNo === targetResultNo) {
    return true;
  }

  const targetCode = target.code != null ? String(target.code) : '';
  const candidateCode = candidate.code != null ? String(candidate.code) : '';
  if (targetCode && candidateCode === targetCode) {
    return true;
  }

  const targetType = normalizeAlphaText(target.type);
  const candidateType = normalizeAlphaText(candidate.type);
  const canMatchByType = ['hotelservicecharges', 'gratuities'].includes(targetType);
  if (canMatchByType && candidateType === targetType) {
    return true;
  }

  const targetDescription = normalizeAlphaText(target.description);
  const candidateDescription = normalizeAlphaText(candidate.description);
  return targetDescription !== ''
    && candidateDescription === targetDescription
    && isTraveltekGratuityExtra(candidate, lineId);
}

export function findSelectedTraveltekGratuity(gratuityExtra, selectedExtras, lineId = 0) {
  if (!gratuityExtra || !Array.isArray(selectedExtras)) {
    return null;
  }

  return selectedExtras.find(item => (
    item && typeof item === 'object' && isSameTraveltekExtra(gratuityExtra, item, lineId)
  )) || null;
}

export function isTraveltekGratuitySelected(gratuityExtra, selectedExtras, lineId = 0) {
  if (!gratuityExtra || !Array.isArray(selectedExtras)) {
    return false;
  }

  return selectedExtras.some(item => isSameTraveltekExtra(gratuityExtra, item, lineId));
}

export function normalizeTraveltekPassengerPriceItem({
  category = '',
  description = '',
  lineId = 0,
} = {}) {
  const normalizedCategory = normalizePriceText(category);
  const normalizedDescription = normalizePriceText(description) || normalizedCategory;

  if (isMscPortChargeDescription(normalizedDescription, lineId)) {
    return {
      category: 'Tax',
      description: 'Tax',
    };
  }

  if (isNonCommissionableFareDescription(normalizedDescription)) {
    return {
      category: normalizedCategory,
      description: 'Cruise Fare',
    };
  }

  return {
    category: normalizedCategory,
    description: normalizedDescription,
  };
}
